import { useEffect, useRef, useState } from "react";

function Counter({ value, suffix = "", duration = 1800 }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!ref.current || started) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setStarted(true);
          obs.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(eased * value));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [started, value, duration]);

  return (
    <span ref={ref} className="tabular-nums">
      {n.toLocaleString()}
      {suffix}
    </span>
  );
}

export default function StatsCounter({ stats }) {
  return (
    <section data-testid="stats-section" className="relative py-20 lg:py-28 ember-bg border-y border-white/10 overflow-hidden">
      <div className="absolute inset-0 grid-lines opacity-50" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ The Numbers</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]">
            A decade of <span className="phoenix-text">movement.</span>
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-px bg-white/5">
          {stats.map((s, i) => (
            <div
              key={i}
              data-testid={`stat-${i}`}
              className="bg-[#0a0a0a] p-6 lg:p-8 hover:bg-[#0f0f0f] transition-colors"
            >
              <div className="font-display text-4xl lg:text-5xl text-white">
                <Counter value={s.value} suffix={s.suffix} />
              </div>
              <p className="text-xs uppercase tracking-wider text-white/50 mt-3">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
